import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import * as Yup from "yup";
import {
  CustomAutocomplete,
  CustomCheckbox,
  CustomDialog,
  CustomInput,
  CustomTextArea,
} from "../../../../../../../../Components";
import { useVerticalNav } from "../../../../../../../../Contexts/VerticalNavContext";
import { useTranslate } from "../../../../../../../../Hooks";
import { Form, Formik } from "formik";
import { Box, Divider, Typography } from "@material-ui/core";
import { EmailIcon, WhatsappIcon } from "../../../../../../../../assets/icons";
import {
  AddShareDocumentLog,
  GetLeads,
  OrganizationUserSearch,
  ShareScopeDocuments,
} from "../../../../../../../../Services";
import { useDebouncedAction } from "../../../../../../../../Hooks/DebouncedAction";
import SelectedChip from "../../../../../../NewContactsView/ContactOverview/UI/ContactDocuments/UI/SelectedChip";
import { LeadsStatusEnum, SharedViaEnum } from "../../../../../../../../Enums";
import { getPublicDownloadableLink } from "../../../../../../../../Helper";

// styles
import useStyle from "./styles";

const getValidationSchemaWhatsApp = ({ translate }) =>
  Yup.object().shape({
    systemUsers: Yup.object()
      .nullable()
      .test(
        "required-if-no-leads",
        translate("Please-selecte-at-least-one-system-user-or-selected-leads"),
        function (value) {
          const { selectedLeads } = this.parent;
          const hasLeads =
            selectedLeads && Object.keys(selectedLeads).length > 0;
          const hasSystemUsers = value && Object.keys(value).length > 0;

          return hasSystemUsers || hasLeads;
        }
      ),

    selectedLeads: Yup.object()
      .nullable()
      .test(
        "required-if-no-users",
        translate("Please-selecte-at-least-one-system-user-or-selected-leads"),
        function (value) {
          const { systemUsers } = this.parent;
          const hasSystemUsers =
            systemUsers && Object.keys(systemUsers).length > 0;
          const hasLeads = value && Object.keys(value).length > 0;

          return hasLeads || hasSystemUsers;
        }
      ),
  });

const getValidationSchemaEmail = ({ translate }) =>
  Yup.object().shape({
    emailSubject: Yup.string().required(translate("Subject-is-required")),
    emailBody: Yup.string().required(translate("Email-Body-is-required")),
    systemUsers: Yup.array().min(
      1,
      translate("At-least-one-user-must-be-selected")
    ),
    selectedLeads: Yup.array(),
  });

const PropertyShareDocumentsDialog = ({ open, onClose, document }) => {
  const formikRef = useRef(null);
  const formRef = useRef(null);
  const styles = useStyle();
  const { setAlertBoxContent } = useVerticalNav();
  const { translate } = useTranslate("NewPropertiesView");

  const reducer = useCallback((state, action) => {
    if (action.id !== "edit") return { ...state, [action.id]: action.value };
    return {
      ...action.value,
    };
  }, []);
  const [isShareViaEmail, setIsShareViaEmail] = useState(true);
  const [selected, setSelected] = useReducer(reducer, {
    emailSubject: null,
    emailBody: null,
    messageBody: null,
    systemUsers: [],
    selectedLeads: [],
  });
  const [data, setData] = useReducer(reducer, {
    systemUsers: [],
    leads: [],
  });
  const debouncedGetLeads = useDebouncedAction(getLeads, { delay: 700 });
  const debouncedGetSystemUsers = useDebouncedAction(getSystemUsers, {
    delay: 700,
  });

  const changeShareType = (value) => {
    const updatedSelected = {
      ...selected,
      systemUsers: value ? [] : null,
      selectedLeads: value ? [] : null,
    };
    setSelected({ id: "edit", value: updatedSelected });
    setIsShareViaEmail(value);
  };

  async function getSystemUsers(value) {
    const res = await OrganizationUserSearch({
      pageIndex: 0,
      pageSize: 10,
      name: value || "",
      userStatusId: 2,
    });
    if (!(res && res.status && res.status !== 200)) {
      const localValue = (res && res.result) || [];
      if (localValue.length > 0) {
        setData({
          id: "systemUsers",
          value: localValue,
        });
      } else {
        setData({
          id: "systemUsers",
          value: [],
        });
      }
    }
  }

  async function getLeads(value) {
    const res = await GetLeads({
      pageIndex: 0,
      pageSize: 10,
      search: value || "",
      leadStatus: LeadsStatusEnum.Open.status,
    });
    if (!(res && res.status && res.status !== 200))
      setData({
        id: "leads",
        value: (res && res.result) || [],
      });
    else setData({ id: "leads", value: [] });
  }

  async function getWhatsAppDocUrl() {
    let URL = "";
    const fileURL = document?.documentId
      ? getPublicDownloadableLink(document?.documentId)
      : "";
    const isLink = document?.documentLink;
    URL = isLink ? document?.documentLink : fileURL;

    const finalURL = `Please find the document you requested here: \n\n${URL}`;
    return finalURL;
  }

  async function fillInitialWhatsappMsgBody() {
    const msg = await getWhatsAppDocUrl();
    setSelected({ id: "messageBody", value: msg });
  }

  function getShareDocsAPIBody() {
    const mappedSystemUsers =
      (selected?.systemUsers &&
        selected?.systemUsers.map((item) => item?.id)) ||
      [];
    const mappedLeads =
      (selected?.selectedLeads &&
        selected?.selectedLeads?.map((item) => item?.leadId)) ||
      [];

    const mappedFiles =
      document && document.documentName && document.documentId
        ? [
            {
              fileName: document.documentName,
              uuid: document.documentId,
            },
          ]
        : [];
    const mappedLinks =
      document && document.documentLink ? [document.documentLink] : [];

    return {
      systemUsersId: mappedSystemUsers,
      leadsId: mappedLeads,
      files: mappedFiles,
      links: mappedLinks,
      emailSubject: selected.emailSubject,
      emailBody: selected.emailBody,
      categoryId: document?.categoryId,
    };
  }

  async function shareViaEmail() {
    const body = getShareDocsAPIBody();

    const res = await ShareScopeDocuments(body, { isFromDeveloper: false });

    if (!(res && res.status && res.status !== 200)) {
      setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate("document-sent-successfully"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      onClose();
    } else {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("sending-document-failed"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    }
  }

  function getShareDocumentLogWahtsAppAPIBody() {
    const SystemUser = selected?.systemUsers?.id
      ? [selected?.systemUsers.id]
      : [];
    const Lead = selected?.selectedLeads?.leadId
      ? [selected?.selectedLeads?.leadId]
      : [];
    const mappedFiles =
      document && document.documentName && document.documentId
        ? [
            {
              fileName: document.documentName,
              uuid: document.documentId,
            },
          ]
        : [];
    const mappedLinks =
      document && document.documentLink ? [document.documentLink] : [];
    return {
      systemUsersId: SystemUser,
      leadsId: Lead,
      files: mappedFiles,
      links: mappedLinks,
      emailSubject: selected.emailBody,
      emailBody: selected.emailBody,
      categoryId: document?.categoryId,
      sharedVia: SharedViaEnum.WhatsApp.id,
    };
  }

  async function shareTempleteViaWhatsAppToDocumentLog() {
    const isWhatsAppLog = true;
    const body = getShareDocumentLogWahtsAppAPIBody();
    const res = await AddShareDocumentLog(body, isWhatsAppLog);
    if (res === true) {
      setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate("template-whats-up-sent-successfully"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      onClose();
    } else {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("template-whats-up-sent-failed"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    }
  }

  function shareViaWhatsApp() {
    const WhatsAppMsg = selected.messageBody || "";
    const UrlEncodedMessage = encodeURIComponent(WhatsAppMsg);
    const whatsAppNumber =
      selected?.systemUsers?.whatsAppNumber ||
      selected.selectedLeads?.contactWhatsApp;

    if (!whatsAppNumber) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("Recipient-s-WhatsApp-number-is-missing"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      return;
    }
    const UrlToWhatsApp = `https://wa.me/${whatsAppNumber}?text=${UrlEncodedMessage}`;
    window.open(UrlToWhatsApp);
    shareTempleteViaWhatsAppToDocumentLog();
  }

  const handleSubmit = async () => {
    if (isShareViaEmail === true) {
      shareViaEmail();
    } else if (isShareViaEmail === false) {
      shareViaWhatsApp();
    }
  };

  useEffect(() => {
    if (!selected.whatsappMsgBody) fillInitialWhatsappMsgBody();
  }, []);

  return (
    <CustomDialog
      open={open}
      onClose={() => {
        onClose();
      }}
      title={translate("share-document")}
      subtitle={translate("share-document-dialog-sub-title")}
      cancelText={translate("cancel")}
      confirmText={translate("proceed")}
      onConfirm={() => formRef.current?.submitForm()}
      width="640px"
      headerBorder
      content={
        <Box className={styles.wrapperpropertyShareDocDialog}>
          <Box className={styles.containerHeaderSelection}>
            <Typography className={styles.containerTitleFiled}>
              {translate("share-via")}
            </Typography>
            <Box className={styles.containerOptions}>
              <Box
                className={`${styles.shareViaItem} ${
                  isShareViaEmail && styles.selectedBox
                }`}
                onClick={() => changeShareType(true)}
              >
                <EmailIcon
                  width="38"
                  height="38"
                  fill={styles.shareViaIconOptionStyles}
                />
                <Typography className={styles.shareViaNameOption}>
                  {translate("email")}
                </Typography>
                <CustomCheckbox
                  className={styles.checkBoxComponent}
                  checked={isShareViaEmail}
                  onChange={() => changeShareType(true)}
                />
              </Box>
              <Box
                className={`${styles.shareViaItem} ${
                  !isShareViaEmail && styles.selectedBox
                }`}
                onClick={() => changeShareType(false)}
              >
                <WhatsappIcon
                  width="38"
                  height="38"
                  fill={styles.shareViaIconOptionStyles}
                />
                <Typography className={styles.shareViaNameOption}>
                  {translate("whatsapp")}
                </Typography>
                <CustomCheckbox
                  className={styles.checkBoxComponent}
                  checked={!isShareViaEmail}
                  onChange={() => changeShareType(false)}
                />
              </Box>
            </Box>
          </Box>
          <Divider />
          {isShareViaEmail && (
            <Formik
              initialValues={{
                emailSubject: null,
                emailBody: null,
                systemUsers: [],
                selectedLeads: [],
              }}
              enableReinitialize
              validationSchema={getValidationSchemaEmail({ translate })}
              onSubmit={handleSubmit}
              innerRef={formikRef}
            >
              {(formikProps) => {
                formRef.current = formikProps;
                const { values, errors, touched, handleSubmit, setFieldValue } =
                  formikProps;

                return (
                  <Form
                    onSubmit={handleSubmit}
                    className={styles.containerFormAddUnitModel}
                  >
                    <Box className={styles.wrapperBodyFiled}>
                      <Box className={styles.containerFiledItem}>
                        <Typography className={styles.containerTitleFiled}>
                          {translate("Email-subject")}
                        </Typography>
                        <CustomInput
                          placeholder={translate("Enter")}
                          hasSearchIcon={false}
                          style={{ maxWidth: "100%" }}
                          value={values.emailSubject}
                          onChange={(e) => {
                            setFieldValue("emailSubject", e.target.value);
                            setSelected({
                              id: "emailSubject",
                              value: e.target.value,
                            });
                          }}
                          hasError={touched.emailSubject && errors.emailSubject}
                          errorMessage={
                            touched.emailSubject && errors.emailSubject
                              ? errors.emailSubject
                              : null
                          }
                          submitted={
                            touched.emailSubject && errors.emailSubject
                          }
                        />
                      </Box>
                      <Divider />
                      <Box className={styles.containerFiledItem}>
                        <Typography className={styles.containerTitleFiled}>
                          {translate("Email-body")}
                        </Typography>
                        <CustomTextArea
                          maxRows={5}
                          minRows={5}
                          placeholder={translate("enter-email-body")}
                          value={values?.emailBody || ""}
                          onInputChange={(value) => {
                            setFieldValue("emailBody", value);
                            setSelected({
                              id: "emailBody",
                              value: value || null,
                            });
                          }}
                          hasError={touched.emailBody && errors.emailBody}
                          errorMessage={
                            touched.emailBody && errors.emailBody
                              ? errors.emailBody
                              : null
                          }
                          submitted={touched.emailBody && errors.emailBody}
                        />
                      </Box>
                      <Divider />
                      <Box className={styles.containerFiledItem}>
                        <Typography className={styles.containerTitleFiled}>
                          {translate("Send-to")}
                        </Typography>
                        <Box className={styles.containerSendToFiled}>
                          <CustomAutocomplete
                            variant="icon"
                            placeholder={translate("select-system-users")}
                            value={values?.systemUsers || []}
                            options={data?.systemUsers || []}
                            renderOption={(option) => option?.fullName || ""}
                            getOptionLabel={(option) => option?.fullName || ""}
                            chipsLabel={(option) => option?.fullName || ""}
                            isDisabled={
                              !isShareViaEmail && selected?.selectedLeads
                            }
                            multiple={true}
                            renderTags={(e, getTagProps) => (
                              <div className={styles.chipsContainer}>
                                {e.map((option, index) => (
                                  <SelectedChip {...getTagProps({ index })}>
                                    {option?.fullName || ""}
                                  </SelectedChip>
                                ))}
                              </div>
                            )}
                            onChange={(e, newValue) => {
                              setSelected({
                                id: "systemUsers",
                                value: newValue || null,
                              });
                              setFieldValue("systemUsers", newValue);
                            }}
                            onInputKeyUp={(e) => {
                              const value = e?.target?.value || null;
                              debouncedGetSystemUsers(value);
                            }}
                            onOpen={() => {
                              if (
                                data.systemUsers &&
                                data.systemUsers.length == 0
                              )
                                getSystemUsers();
                            }}
                            hasError={touched.systemUsers && errors.systemUsers}
                            errorMessage={
                              touched.systemUsers && errors.systemUsers
                                ? errors.systemUsers
                                : null
                            }
                            submitted={
                              touched.systemUsers && errors.systemUsers
                            }
                          />
                          <CustomAutocomplete
                            variant="icon"
                            placeholder={translate("select-leads")}
                            value={values?.selectedLeads || []}
                            options={data?.leads || []}
                            isDisabled={
                              !isShareViaEmail && selected?.systemUsers
                            }
                            renderOption={(option) =>
                              option?.lead?.contact_name?.name || ""
                            }
                            chipsLabel={(option) =>
                              option?.lead?.contact_name?.name || ""
                            }
                            getOptionLabel={(option) =>
                              option?.lead?.contact_name?.name || ""
                            }
                            multiple={true}
                            renderTags={(e, getTagProps) => (
                              <div className={styles.chipsContainer}>
                                {e.map((option, index) => (
                                  <SelectedChip {...getTagProps({ index })}>
                                    {option?.lead?.contact_name?.name || ""}
                                  </SelectedChip>
                                ))}
                              </div>
                            )}
                            onChange={(e, newValue) => {
                              setSelected({
                                id: "selectedLeads",
                                value: newValue || null,
                              });
                              setFieldValue("selectedLeads", newValue);
                            }}
                            onInputKeyUp={(e) => {
                              const value = e?.target?.value || null;
                              debouncedGetLeads(value);
                            }}
                            onOpen={() => {
                              if (data?.leads?.length === 0) getLeads();
                            }}
                            hasError={
                              touched.selectedLeads && errors.selectedLeads
                            }
                            errorMessage={
                              touched.selectedLeads && errors.selectedLeads
                                ? errors.selectedLeads
                                : null
                            }
                            submitted={
                              touched.selectedLeads && errors.selectedLeads
                            }
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          )}

          {!isShareViaEmail && (
            <Formik
              initialValues={{
                messageBody: selected.messageBody || "",
                systemUsers: null,
                selectedLeads: null,
              }}
              enableReinitialize
              validationSchema={getValidationSchemaWhatsApp({ translate })}
              onSubmit={handleSubmit}
              innerRef={formikRef}
            >
              {(formikProps) => {
                formRef.current = formikProps;
                const { values, errors, touched, handleSubmit, setFieldValue } =
                  formikProps;

                return (
                  <Form
                    onSubmit={handleSubmit}
                    className={styles.containerFormAddUnitModel}
                  >
                    <Box className={styles.wrapperBodyFiled}>
                      <Box className={styles.containerFiledItem}>
                        <Typography className={styles.containerTitleFiled}>
                          {translate("message-body")}
                        </Typography>
                        <CustomTextArea
                          maxRows={5}
                          minRows={5}
                          placeholder={translate("enter-message-body")}
                          value={values?.messageBody || ""}
                          onInputChange={(value) => {
                            setFieldValue("messageBody", value);
                            setSelected({
                              id: "messageBody",
                              value: value || null,
                            });
                          }}
                        />
                      </Box>
                      <Divider />
                      <Box className={styles.containerFiledItem}>
                        <Typography className={styles.containerTitleFiled}>
                          {translate("Send-to")}
                        </Typography>
                        <Box className={styles.containerSendToFiled}>
                          <CustomAutocomplete
                            variant="icon"
                            placeholder={translate("select-system-users")}
                            value={values?.systemUsers || null}
                            options={data?.systemUsers || []}
                            renderOption={(option) => option?.fullName || ""}
                            getOptionLabel={(option) => option?.fullName || ""}
                            chipsLabel={(option) => option?.fullName || ""}
                            isDisabled={
                              !isShareViaEmail && selected?.selectedLeads
                            }
                            multiple={false}
                            renderTags={(e, getTagProps) => (
                              <div className={styles.chipsContainer}>
                                {e.map((option, index) => (
                                  <SelectedChip {...getTagProps({ index })}>
                                    {option?.fullName || ""}
                                  </SelectedChip>
                                ))}
                              </div>
                            )}
                            onChange={(e, newValue) => {
                              setSelected({
                                id: "systemUsers",
                                value: newValue || null,
                              });
                              setFieldValue("systemUsers", newValue || null);
                            }}
                            onInputKeyUp={(e) => {
                              const value = e?.target?.value || null;
                              debouncedGetSystemUsers(value);
                            }}
                            onOpen={() => {
                              if (!data?.systemUsers?.length) getSystemUsers();
                            }}
                            hasError={touched.systemUsers && errors.systemUsers}
                            errorMessage={
                              touched.systemUsers && errors.systemUsers
                                ? errors.systemUsers
                                : null
                            }
                            submitted={
                              touched.systemUsers && errors.systemUsers
                            }
                          />
                          <CustomAutocomplete
                            variant="icon"
                            placeholder={translate("select-leads")}
                            value={values?.selectedLeads || null}
                            options={data?.leads || []}
                            renderOption={(option) =>
                              option?.lead?.contact_name?.name || ""
                            }
                            getOptionLabel={(option) =>
                              option?.lead?.contact_name?.name || ""
                            }
                            chipsLabel={(option) =>
                              option?.lead?.contact_name?.name || ""
                            }
                            isDisabled={
                              !isShareViaEmail && selected?.systemUsers
                            }
                            multiple={false}
                            renderTags={(e, getTagProps) => (
                              <div className={styles.chipsContainer}>
                                {e.map((option, index) => (
                                  <SelectedChip {...getTagProps({ index })}>
                                    {option?.lead?.contact_name?.name || ""}
                                  </SelectedChip>
                                ))}
                              </div>
                            )}
                            onChange={(e, newValue) => {
                              setSelected({
                                id: "selectedLeads",
                                value: newValue || null,
                              });
                              setFieldValue("selectedLeads", newValue || null);
                            }}
                            onInputKeyUp={(e) => {
                              const value = e?.target?.value || null;
                              debouncedGetLeads(value);
                            }}
                            onOpen={() => {
                              if (!data?.leads?.length) getLeads();
                            }}
                            hasError={
                              touched.selectedLeads && errors.selectedLeads
                            }
                            errorMessage={
                              touched.selectedLeads && errors.selectedLeads
                                ? errors.selectedLeads
                                : null
                            }
                            submitted={
                              touched.selectedLeads && errors.selectedLeads
                            }
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          )}
        </Box>
      }
    />
  );
};

export default PropertyShareDocumentsDialog;
